import React, { useEffect } from "react";

const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen py-10 px-4 md:px-10">
      <div className="max-w-5xl mx-auto bg-white shadow-sm border border-gray-200 rounded-lg p-8 md:p-12 text-[15px] leading-relaxed text-gray-700">
        
        {/* Header */}
        <div className="border-b pb-6 mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-2 uppercase tracking-tight">Terms and Conditions</h1>
          <p className="text-sm text-gray-600 mt-2">
            Website: <strong>https://mediiapp.in</strong>
          </p>
          <p className="text-sm text-gray-500 italic mt-2">
            Please read these terms carefully. By accessing or using this platform, you agree to be bound by these terms and conditions.
          </p>
          <p className="text-sm text-gray-600 mt-4 font-semibold">Last Updated: March 2026</p>
        </div>

        {/* 1. About Us / Business Information */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            1. About Us
          </h2>
          <p className="mb-4">
            The website <strong>https://mediiapp.in</strong> and the mobile application <strong>Mediiapp</strong> (collectively referred to as the "Platform") are owned, operated, and managed by:
          </p>
          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mb-4">
            <p className="font-bold text-gray-900">Maya Enterprises</p>
            <p>Registered Address: GF-39, Omaxe Square, Jasola, New Delhi - 110025, India</p>
            <p>Contact Person: Dheeraj Khatwani, Director</p>
            <p>Email: <span className="text-green-600 font-bold">Mediiappindia@gmail.com</span></p>
            <p>Phone: <span className="text-green-600 font-bold">+91 8588055556</span> (Technical Support) | <span className="text-green-600 font-bold">+91 9873320785</span> (Sales Enquiries)</p>
          </div>
          <p>
            Maya Enterprises is a licensed wholesale dealer of pharmaceuticals, specializing in Kidney and Liver transplant medicines. Through our Platform, we facilitate a Patient Assistance Program (PAP) to ensure patients receive essential medications under professional medical guidelines.
          </p>
        </section>

        {/* 2. Nature of Products and Services */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            2. Products and Services
          </h2>
          <p className="mb-4">
            Mediiapp provides access to specialty pharmaceutical products, specifically life-saving medicines for Kidney and Liver transplant patients. The Platform acts as an authorized distribution channel for these medications.
          </p>
          <ul className="list-disc ml-6 space-y-2">
            <li>All products listed on the Platform are genuine, sourced directly from authorized manufacturers and distributors.</li>
            <li>Product prices displayed on the Platform are inclusive of applicable taxes unless stated otherwise.</li>
            <li>Prices are subject to change without prior notice due to market conditions and manufacturer pricing updates.</li>
            <li>The final billing amount will be confirmed at the time of order placement after prescription verification.</li>
          </ul>
        </section>

        {/* 3. Prescription Requirement */}
        <section className="mb-10 bg-green-50 p-6 rounded-lg border border-green-200">
          <h2 className="text-xl font-bold text-green-900 mb-3">3. Mandatory Prescription Policy</h2>
          <p className="text-green-800 mb-4">
            In compliance with the Drugs and Cosmetics Act, 1940, and all applicable Indian pharmaceutical regulations, Maya Enterprises will only process orders for specialty medicines upon receipt of a valid medical prescription issued by a registered medical practitioner.
          </p>
          <ul className="list-disc ml-6 space-y-2 text-green-800">
            <li>A scanned copy or clear photograph of the prescription must be uploaded during or after the order placement.</li>
            <li>We reserve the right to verify prescriptions with the issuing doctor or hospital before shipment.</li>
            <li>Orders without valid prescriptions will not be processed and any payment collected will be refunded.</li>
          </ul>
        </section>

        {/* 4. Pricing and Payment Terms */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            4. Pricing and Payment Terms
          </h2>
          <p className="mb-4">
            All payments on the Platform are processed securely through our authorized payment gateway partner, <strong>Razorpay</strong>. By making a payment, you agree to the terms of the payment gateway provider as well.
          </p>
          <ul className="list-disc ml-6 space-y-2">
            <li><strong>Accepted Payment Methods:</strong> UPI, Net Banking, Debit Cards, and Credit Cards.</li>
            <li><strong>Currency:</strong> All transactions are processed in Indian Rupees (INR).</li>
            <li>All payments are subject to verification and approval by the respective banks or payment service providers.</li>
            <li>In case of payment failure or deduction without order confirmation, the amount will be automatically refunded to your source account within 5-7 working days.</li>
            <li>We do not store any payment card details on our servers. All payment data is handled securely by Razorpay in compliance with PCI-DSS standards.</li>
          </ul>
        </section>

        {/* 5. Shipping and Delivery */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            5. Shipping and Delivery Policy
          </h2>
          <p className="mb-4">
            We aim to deliver life-saving transplant medications across India within <strong>3 to 7 working days</strong> from the date of successful prescription verification and payment confirmation.
          </p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Shipments are processed through specialized cold-chain logistics where required to maintain medicine integrity.</li>
            <li>Delivery timelines may vary based on the destination, courier availability, and any regulatory requirements.</li>
            <li>Tracking details will be provided via email and/or SMS once the order is dispatched.</li>
            <li>In case of non-delivery or delivery to a wrong address due to incorrect information provided by the customer, Maya Enterprises will not be held responsible.</li>
            <li>Shipping charges, if any, will be communicated at the time of checkout.</li>
          </ul>
        </section>

        {/* 6. Cancellation and Refund */}
        <section className="mb-10 bg-red-50 p-6 rounded-lg border border-red-200">
          <h2 className="text-xl font-bold text-red-900 mb-3">6. Cancellation and Refund Policy</h2>
          <p className="text-red-800 mb-4">
            Due to the critical and sensitive nature of transplant medications, we maintain a strict policy regarding cancellations, returns, and refunds:
          </p>
          <ul className="list-disc ml-6 space-y-3 text-red-800">
            <li><strong>Cancellations:</strong> Orders may be cancelled only before they are dispatched. To request cancellation, contact us immediately at <strong>Mediiappindia@gmail.com</strong>.</li>
            <li><strong>Returns:</strong> Medicines cannot be returned once delivered, in order to ensure product integrity, safety, and compliance with pharmaceutical regulations.</li>
            <li><strong>Damaged or Wrong Products:</strong> If you receive a damaged or incorrect product, please notify us within 24 hours of delivery with photographic evidence. We will arrange a replacement or full refund after investigation.</li>
            <li><strong>Refund Processing:</strong> Approved refunds will be credited to the original payment method within <strong>5-7 working days</strong> from the date of approval. Refund timelines may vary depending on your bank or payment provider.</li>
            <li><strong>Payment Failures:</strong> In case of payment deduction without order confirmation, the amount will be automatically reversed within 5-7 working days.</li>
          </ul>
        </section>

        {/* 7. User Responsibilities */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            7. User Responsibilities
          </h2>
          <ul className="list-disc ml-6 space-y-2">
            <li>You must provide accurate, current, and complete information while placing orders, including a valid delivery address and contact number.</li>
            <li>You must upload a genuine prescription from a registered medical practitioner.</li>
            <li>You shall not misuse the Platform for any unlawful or unauthorized purpose.</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
          </ul>
        </section>

        {/* 8. Privacy and Data Protection */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            8. Privacy and Data Protection
          </h2>
          <p className="mb-4">
            We are committed to protecting your personal and medical information. By using the Platform, you consent to the collection and use of your data as outlined below:
          </p>
          <ul className="list-disc ml-6 space-y-2">
            <li>We collect personal details such as name, address, phone number, email, and prescription data solely for order processing and delivery.</li>
            <li>Your data will not be shared with any third party except as necessary for order fulfillment (e.g., courier partners, payment gateway).</li>
            <li>Payment information is processed securely by Razorpay and is not stored on our servers.</li>
            <li>We implement industry-standard security measures to protect your data from unauthorized access.</li>
            <li>For detailed information, please refer to our Privacy Policy page.</li>
          </ul>
        </section>

        {/* 9. Medical Disclaimer */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            9. Medical Disclaimer
          </h2>
          <p className="bg-yellow-50 p-4 border-l-4 border-yellow-400 text-gray-800">
            <strong>Important:</strong> The information provided on Mediiapp is for general awareness and informational purposes only. It does not constitute medical advice, diagnosis, or treatment. Maya Enterprises is a pharmaceutical distributor and is not liable for any clinical outcomes. Always consult your specialist or healthcare professional before making any medical decisions.
          </p>
        </section>

        {/* 10. Limitation of Liability */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            10. Limitation of Liability
          </h2>
          <p>
            Maya Enterprises shall not be liable for any indirect, incidental, or consequential damages arising from the use of the Platform or the products purchased through it. Our total liability shall not exceed the amount paid by the customer for the specific order in question.
          </p>
        </section>

        {/* 11. Modifications to Terms */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            11. Modifications to Terms
          </h2>
          <p>
            Maya Enterprises reserves the right to update or modify these Terms and Conditions at any time without prior notice. Any changes will be effective immediately upon posting on this page. Your continued use of the Platform after changes are posted constitutes your acceptance of the revised terms.
          </p>
        </section>

        {/* 12. Governing Law */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-l-4 border-green-600 pl-3">
            12. Governing Law and Jurisdiction
          </h2>
          <p>
            These Terms and Conditions are governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these terms shall be subject to the exclusive jurisdiction of the courts in <strong>New Delhi, India</strong>.
          </p>
        </section>

        {/* Contact / Footer */}
        <section className="mt-10 pt-8 border-t border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <p className="mb-2">If you have any questions or concerns regarding these Terms and Conditions, please contact us:</p>
          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
            <p className="font-bold text-gray-900">Maya Enterprises</p>
            <p>Contact Person: Dheeraj Khatwani, Director</p>
            <p>Address: GF-39, Omaxe Square, Jasola, New Delhi - 110025, India</p>
            <p>Email: <span className="text-green-600 font-bold">Mediiappindia@gmail.com</span></p>
            <p>Phone: <span className="text-green-600 font-bold">+91 8588055556</span> (Technical Support) | <span className="text-green-600 font-bold">+91 9873320785</span> (Sales Enquiries)</p>
            <p>Website: <span className="text-green-600 font-bold">https://mediiapp.in</span></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TermsConditions;